import { type ChangeEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  FormBuilderSmileyScale1,
  FormBuilderSmileyScale2,
  FormBuilderSmileyScale3,
  FormBuilderSmileyScale4,
  FormBuilderSmileyScale5
} from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { getColorWithAlpha, isTransparentColor } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import { commonFieldsSettings } from '../commonFieldSettings';
import { SMILEY_SCALE_VALUES } from '../constants';
import type { CommonFieldProps } from '../types';

const colors = ['#EF4623', '#F68E20', '#FECC08', '#91E53E', '#0EC12F'];
const icons = [
  FormBuilderSmileyScale1,
  FormBuilderSmileyScale2,
  FormBuilderSmileyScale3,
  FormBuilderSmileyScale4,
  FormBuilderSmileyScale5
];

export function SmileyScale({
  label,
  onChange,
  onFocus,
  value,
  fieldFillColor = commonFieldsSettings.fieldFillColor,
  fieldColor = commonFieldsSettings.fieldColor,
  required
}: CommonFieldProps) {
  const [checkedIndex, setCheckedIndex] = useState(-1);
  const [hoverIndex, setHoverIndex] = useState(-1);

  const handleMouseOver = (i: number) => setHoverIndex(i);
  const handleCheck = (event: ChangeEvent<HTMLInputElement>, i: number) => {
    const { value } = event.target;
    onChange?.(value, event);
    setCheckedIndex(i);
  };

  useEffect(() => {
    if (value) {
      setCheckedIndex(SMILEY_SCALE_VALUES.indexOf(value));
    }
  }, [value]);

  const fillColor = isTransparentColor(fieldFillColor)
    ? getColorWithAlpha(fieldColor, 0.1)
    : fieldFillColor;

  return (
    <SmilesContainer role="radiogroup" aria-required={required}>
      {SMILEY_SCALE_VALUES.map((_, index) => (
        <IconContainer
          key={index}
          onMouseEnter={() => handleMouseOver(index)}
          onMouseLeave={() => handleMouseOver(-1)}
          _isChecked={checkedIndex === index}
        >
          {SMILEY_SCALE_VALUES[index]}
          <Input
            type="radio"
            name={label}
            value={SMILEY_SCALE_VALUES[index]}
            onChange={(e) => handleCheck(e, index)}
            onFocus={onFocus}
          />
          <StyledIcon
            component={icons[index]}
            size={48}
            _fillColor={fillColor}
            _fieldColor={fieldColor}
            _activeColor={colors[index]}
            _index={index}
            _checkedIndex={checkedIndex}
            _hoverIndex={hoverIndex}
          />
        </IconContainer>
      ))}
    </SmilesContainer>
  );
}

const SmilesContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'smiley-scale-container'
  })
})`
  display: flex;
`;

const IconContainer = styled.label.attrs<{
  _isChecked: boolean;
}>(({ _isChecked }) => ({
  className: createReadableClass({
    component: 'fields',
    element: 'smiley-scale-icon-container',
    index: _isChecked && 'checked'
  })
}))`
  font-size: 0;
  line-height: 0;
  position: relative;
  &:not(:last-child) {
    margin-right: 8px;
  }
`;

const Input = styled.input.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'smiley-scale-input'
  })
})`
  position: absolute;
  cursor: pointer;
  margin: 0;
  opacity: 0;
  width: 48px;
  height: 48px;
`;

interface StyledIconProps {
  _fillColor: string;
  _fieldColor: string;
  _activeColor: string;
  _index: number;
  _checkedIndex: number;
  _hoverIndex: number;
}

const StyledIcon = styled(Icon).attrs<StyledIconProps>({
  className: createReadableClass({
    component: 'fields',
    element: 'smiley-scale-icon'
  })
})`
  & path:first-child {
    transition: 0.1s all ease;
    fill: ${({
      _fillColor,
      _activeColor,
      _index,
      _checkedIndex,
      _hoverIndex
    }) =>
      _index === _hoverIndex || _index === _checkedIndex
        ? _activeColor
        : _fillColor};
  }
  & path:last-child {
    transition: 0.1s all ease;
    fill: ${({ _fieldColor, _index, _checkedIndex, _hoverIndex }) =>
      _index === _hoverIndex || _index === _checkedIndex
        ? 'rgb(0, 0, 0)'
        : _fieldColor};
  }
`;
